import { useCallback, useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import type { ProductView } from '../../../../services/products/products.service';
import type { VariantComboGroupView } from '../../../../services/variants-combos/variants-combos.service';
import type { UiGroup } from '../../components/settings/options-builder/options-builder.utils';
import type { useProductCompositionEditor } from './useProductCompositionEditor';

type CompositionEditor = ReturnType<typeof useProductCompositionEditor>;

type Params = {
  employeeSession: string | null;
  settingsModalKey: string | null;
  composition: CompositionEditor;
  invalidateComposition: (productId: number) => void;
};

// Architectural summary: Owns the manager-side variants/combos Options Builder drawer (open/save/close),
export function useProductOptions({
  employeeSession,
  settingsModalKey,
  composition,
  invalidateComposition,
}: Params) {
  const { loadAllGroups, applyVariantComboGroupUpserted, applyVariantComboGroupRemoved } = composition;

  const [optionsProduct, setOptionsProduct] = useState<ProductView | null>(null);
  const [variantComboCountByProductId, setVariantComboCountByProductId] = useState<Record<number, number>>({});
  const [isCopyingGroup, setIsCopyingGroup] = useState(false);
  // Root group ids per product, kept in sync with variantComboCountByProductId — lets SSE deltas
  // patch the count locally (Rule 27: SSE handlers must patch from the payload, never refetch)
  // instead of re-fetching the tenant-wide aggregate on every edit. Seeded once from a real fetch
  // when ניהול מוצרים opens; every event after that is a pure local set update, no network call.
  const rootGroupIdsByProductIdRef = useRef<Record<number, Set<number>>>({});

  const reloadVariantComboCounts = useCallback(async () => {
    if (!employeeSession) {
      return;
    }
    const allGroups = await loadAllGroups();
    const idsByProduct: Record<number, Set<number>> = {};
    for (const group of allGroups) {
      if (group.parentItemId !== null || group.parentProductId === null) {
        continue;
      }
      const set = idsByProduct[group.parentProductId] ?? new Set<number>();
      set.add(group.id);
      idsByProduct[group.parentProductId] = set;
    }
    rootGroupIdsByProductIdRef.current = idsByProduct;
    setVariantComboCountByProductId(
      Object.fromEntries(Object.entries(idsByProduct).map(([productId, ids]) => [productId, ids.size])),
    );
  }, [employeeSession, loadAllGroups]);

  useEffect(() => {
    if (settingsModalKey === 'products') {
      void reloadVariantComboCounts();
    }
  }, [settingsModalKey, reloadVariantComboCounts]);

  const applyVariantComboUpsertedDelta = useCallback(
    (productId: number, group: VariantComboGroupView) => {
      invalidateComposition(productId);
      applyVariantComboGroupUpserted(productId, group);
      // Nested groups (attached to a parent item, not directly to the product) never count toward
      // the badge — only patch state for root groups.
      if (group.parentItemId !== null) {
        return;
      }
      const known = rootGroupIdsByProductIdRef.current[productId] ?? new Set<number>();
      if (known.has(group.id)) {
        return; // edit to an already-known root group — count unchanged
      }
      const next = new Set(known);
      next.add(group.id);
      rootGroupIdsByProductIdRef.current = { ...rootGroupIdsByProductIdRef.current, [productId]: next };
      setVariantComboCountByProductId((prev) => ({ ...prev, [productId]: next.size }));
    },
    [invalidateComposition, applyVariantComboGroupUpserted],
  );
  const applyVariantComboRemovedDelta = useCallback(
    (productId: number, groupId: number) => {
      invalidateComposition(productId);
      applyVariantComboGroupRemoved(productId, groupId);
      const known = rootGroupIdsByProductIdRef.current[productId];
      if (!known || !known.has(groupId)) {
        return;
      }
      const next = new Set(known);
      next.delete(groupId);
      rootGroupIdsByProductIdRef.current = { ...rootGroupIdsByProductIdRef.current, [productId]: next };
      setVariantComboCountByProductId((prev) => ({ ...prev, [productId]: next.size }));
    },
    [invalidateComposition, applyVariantComboGroupRemoved],
  );

  const openProductOptions = useCallback((product: ProductView) => {
    setOptionsProduct(product);
    void composition.loadComposition(product.id);
  }, [composition]);

  const closeProductOptions = useCallback(() => {
    setOptionsProduct(null);
    composition.clearComposition();
  }, [composition]);

  const handleSaveProductOptions = useCallback(
    async (uiGroups: Parameters<typeof composition.commitDraft>[1]) => {
      if (!optionsProduct) {
        return;
      }
      try {
        await composition.commitDraft(optionsProduct.id, uiGroups);
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : '';
        notifications.show({
          message: message.includes('not active tenant printers')
            ? 'לא ניתן לשמור: המדפסת שנבחרה לא זמינה'
            : 'לא ניתן לשמור את השינויים',
          color: 'red',
          autoClose: 4000,
        });
        return;
      }
      // The badge count is no longer optimistically set here — batchUpsertProductGroups publishes
      // variantComboGroup.upserted/removed for every affected group (including back to the
      // originating device), and applyVariantComboUpsertedDelta/RemovedDelta above patch the count
      // from those real events. This also avoids showing a count that hasn't actually been
      // persisted if the save is rejected.
      setOptionsProduct(null);
      composition.clearComposition();
      notifications.show({ message: 'השינויים נשמרו', autoClose: 3000 });
    },
    [composition, optionsProduct],
  );

  const copyInFlightRef = useRef(false);
  const handleCopyGroupToProducts = useCallback(
    async (group: UiGroup, targetProductIds: number[]) => {
      if (copyInFlightRef.current || targetProductIds.length === 0) {
        return;
      }
      copyInFlightRef.current = true;
      setIsCopyingGroup(true);
      try {
        await composition.copyGroupToProducts(targetProductIds, group);
        notifications.show({
          message: targetProductIds.length === 1 ? 'הקבוצה הועתקה למוצר' : `הקבוצה הועתקה ל-${targetProductIds.length} מוצרים`,
          autoClose: 3000,
        });
      } catch {
        notifications.show({ message: 'לא ניתן להעתיק את הקבוצה', color: 'red', autoClose: 4000 });
      } finally {
        copyInFlightRef.current = false;
        setIsCopyingGroup(false);
      }
    },
    [composition],
  );

  return {
    optionsProduct,
    variantComboCountByProductId,
    isCopyingGroup,
    reloadVariantComboCounts,
    openProductOptions,
    closeProductOptions,
    handleSaveProductOptions,
    handleCopyGroupToProducts,
    applyVariantComboGroupUpserted: applyVariantComboUpsertedDelta,
    applyVariantComboGroupRemoved: applyVariantComboRemovedDelta,
  };
}
